import { carSelectors, financialStatementsSelectors, } from '~/store';
import { getNewEmptyBalanceSheetObj } from '~/models/balance-sheet';
import { getNewEmptyIncomeStatementObj } from '~/models/income-statement';
import {
	getFinancialStatementsFor,
	getSystemFinancialStatementsFor,
} from '~/store/financial-statements/selectors';
// ---- internal imports -----

/* eslint-disable max-len */
const ERROR_MESSAGES = {
	incomeStatement: {
		revenue: 'You may have not accounted for all items sold, used wrong prices in your calculations or miscalculated the total value.',
		openingInventory: 'You may have not accounted for all items purchased, used wrong prices in your calculations or miscalculated the total value.',
		purchases: 'You may have not accounted for all items purchased, used wrong prices in your calculations or miscalculated the total value.',
		closingInventory: (hasLeasedCar) => {
			return hasLeasedCar
				? 'Cargo car affects the price at which you buy stock> Make sure you have the pricing and quantities correct.' 
				: 'You may have not accounted for all items in stock, used wrong figures or perhaps miscalculated the total value.';
		},
		lostInventory: 'Inventory lost = #items lost * value of items',
		costOfGoodsSold: (hasLostInventory) => {
			return hasLostInventory
				? 'COGS = opening inventory + purchases - closing inventory - lost inventory'
				: 'There seems to be a calculation error. COGS = opening inventory + purchases - closing inventory' ;
		},
		grossProfit: 'There seems to be a calculation error.',
		expenses: 'You may have not accounted for all expenses or miscalculated the total value.',
		netProfit: 'Profit = Revenue - COGS - Expenses',
	},
	balanceSheet: {
		currentCash: 'Be sure to add this months expenses to current cash balance.',
		accountReceivables: 'You may have forgotten to add accounts receivable of the new month or there might have been a miscalculation.',
		currentInventory: 'Double check you have copied the value correctly.',
		prepaidExpense: 'Make sure to calculate all Tradeshows prices correctly.',
		totalAssets: 'total assets = fixed assets + current assets.',
		retainedEarnings: 'Use the wallet to check for value of retained earnings available for distribution.' ,
		profit: 'Make sure you correctly copied profit from income statement',
		profitDistribution: 'Make sure you added all profits you distributed during the last quarter',
		fixedAssets: 'You don\'t have any fixed assets.',
		currentLiabilities: 'You don\'t have any liabilities.',
		longTermLiabilities: 'You don\'t have any liabilities.',
		totalEquity: 'This should be equal to Total Assets as you don\'t have any liabilities',
		totalLiabilitiesAndEquity: 'This should be equal to Total Assets',
	},
};
/* eslint-enable max-len */

function getEmptyStatement(statement) {
	switch(statement) {
	case 'incomeStatement':
		return getNewEmptyIncomeStatementObj();
	case 'balanceSheet':
		return getNewEmptyBalanceSheetObj();
	default:
		return {};
	}
}

function getComparableFields(statement, keys) {    
	if (statement === 'incomeStatement') {
		return keys;
	}

	if (statement === 'balanceSheet') {
		return  keys.filter((key) => { return key !== 'contributedCapital'; });
	}
}

function getFieldToFix(state, quarterNum, statement, updatedUserStatement) {
	let userStatement = null;

	if (updatedUserStatement === null) {
		userStatement = getFinancialStatementsFor(state, quarterNum)[statement];
	}
	else {
		userStatement = updatedUserStatement;
	}

	if (userStatement === null || userStatement === undefined) {
		userStatement = getEmptyStatement(statement);
	}

	const systemStatement = getSystemFinancialStatementsFor(state, quarterNum)[statement];
	const comparableFields = 
		getComparableFields(statement, Object.keys(userStatement));

	let fieldWithError = '';

	for (let key of comparableFields) {
		if (systemStatement[key] != userStatement[key]) {
			fieldWithError = key;
			break;
		}
	}

	return fieldWithError;
}

function getFieldErrorMessage(state, statement, quarterNum, field) {
	if (field.length === 0) {
		return '';
	}

	const errorMessage = ERROR_MESSAGES[statement][field];

	if (typeof errorMessage === 'string') {
		return errorMessage;
	}

	switch(field) {
	case 'closingInventory':
		const hasLeasedCar = carSelectors.hasLeasedCar(state);
		return errorMessage(hasLeasedCar);
	case 'costOfGoodsSold':
		const hasLostInventory = financialStatementsSelectors
			    .hasLostInventoryForQuarter(state, quarterNum);
		return errorMessage(hasLostInventory);
	default:
		return '';
	}
};

export function getErrorConfigurationFor(
	state,
	quarterNum,
	statementType,
	updatedUserStatement = null
) {
	const fieldToFix = getFieldToFix(state, quarterNum, statementType, updatedUserStatement);
	
	return { 
		fieldToFix,
		message: getFieldErrorMessage(state, statementType, quarterNum, fieldToFix)
	};
}

export function getEmptyErrorConfiguration() {
	return {
		statement: '',
		message: '',
		fieldToFix: '',
	};
}

export function getErrorConfiguration(state, quarterNum, statementType = null, statement = null) {
	const incomeStatementErrorConfiguration = statementType === 'incomeStatement'
		? getErrorConfigurationFor(state, quarterNum, 'incomeStatement', statement)
		: getErrorConfigurationFor(state, quarterNum, 'incomeStatement');

	if (incomeStatementErrorConfiguration.fieldToFix) {
		return {
			statement: 'incomeStatement',
			...incomeStatementErrorConfiguration,
		};
	}

	const balanceSheetErrorConfiguration = statementType === 'balanceSheet'
		? getErrorConfigurationFor(state, quarterNum, 'balanceSheet', statement)
		: getErrorConfigurationFor(state, quarterNum, 'balanceSheet');

	if (balanceSheetErrorConfiguration.fieldToFix) {
		return {
			statement: 'balanceSheet',
			...balanceSheetErrorConfiguration,
		};
	}

	return getEmptyErrorConfiguration();
}

export function getErrorConfigurationAfterStatementUpdate(
	state,
	quarterNum,
	statementType,
	statement
) {
	return getErrorConfiguration(state, quarterNum, statementType, statement);
}
